import { browserLogger } from "@@/settings";
import type {
    Translatable,
    TranslatableValueFactoryParams,
} from "@towni/common";
import { useEffect, useState } from "react";
import { useCartContext } from "./multi-carts/cart.context";

type Result = {
    verified: boolean;
    message: Translatable | TranslatableValueFactoryParams;
};
const useVerifyCartProductForms = (): Result => {
    const cartContext = useCartContext(context => ({
        cart: context.cart,
        productForms: context.productForms,
        productFormsPending: context.productFormsPending,
    }));

    const [result, setResult] = useState<Result>({
        verified: false,
        message: {
            sv: "Kontrollerar om det finns kundformulär som behöver fyllas i",
            en: "Checking if there are customer forms that need to be filled in",
            de: "Überprüfen, ob Kundenformulare ausgefüllt werden müssen",
        },
    });

    useEffect(() => {
        if (cartContext.productFormsPending) {
            // When product forms are undefined in the cart it means that
            // the product forms have not been fetched yet and we should
            // wait for them to be fetched before
            // we can check if the order can be placed
            setResult({
                verified: false,
                message: {
                    sv: "Kontrollerar om det finns kundformulär som behöver fyllas i",
                    en: "Checking if there are customer forms that need to be filled in",
                    de: "Überprüfen, ob Kundenformulare ausgefüllt werden müssen",
                },
            });
            return;
        }

        if (
            cartContext.productForms.length &&
            cartContext.productForms.some(pf => !!pf.fields.length)
        ) {
            const answers = cartContext.cart.productFormAnswers ?? [];
            // If there are product forms,
            // Check for forms that have required fields..
            const formsWithRequiredFields = cartContext.productForms.filter(
                pf => pf.fields.some(f => f.required),
            );

            //  And make sure that all required fields are filled in
            const hasUnfinishedForms = formsWithRequiredFields.some(item => {
                return !answers.some(
                    result => result.productFormId === item._id,
                );
            });

            browserLogger.info("hasUnfinishedForms", {
                hasUnfinishedForms,
                formsWithRequiredFields,
                productForms: cartContext.productForms,
                productFormAnswers: answers,
                keys: Object.keys(answers),
            });

            if (hasUnfinishedForms) {
                setResult({
                    verified: false,
                    message: {
                        sv: "Fyll i alla obligatoriska fält",
                        en: "Please fill in all required fields",
                        de: "Bitte füllen Sie alle Pflichtfelder aus",
                    },
                });
                return;
            }

            // All is well in the world
            setResult({
                verified: true,
                message: null,
            });
        }

        // All is well in the world
        setResult({
            verified: true,
            message: null,
        });
    }, [
        cartContext.productForms,
        cartContext.cart.productFormAnswers,
        cartContext.productFormsPending,
        cartContext.cart,
    ]);

    return result;
};

export { useVerifyCartProductForms };
